/**
 * Payment Logger Utility
 *
 * MOTIVATION:
 * Track every step of the payment flow to diagnose 3D Secure issues where users
 * complete authentication but return to a blank page without course creation.
 *
 * This utility provides:
 * 1. Detailed Sentry logging for each payment step
 * 2. Breadcrumbs for debugging user journey
 * 3. Context capture (client, zone, amount, etc.)
 * 4. Error tracking with full stack traces
 *
 * USAGE:
 * Import and call at critical points in the payment flow
 */

import * as Sentry from '@sentry/react';

/**
 * Payment Flow Steps - Enum for consistent tracking
 */
export const PAYMENT_STEPS = {
  // Initial steps
  PAYMENT_PAGE_LOADED: 'payment_page_loaded',
  CARDS_FETCHED: 'cards_fetched',
  CARD_SELECTED: 'card_selected',

  // Stripe Setup Intent steps
  SETUP_INTENT_REQUESTED: 'setup_intent_requested',
  SETUP_INTENT_RECEIVED: 'setup_intent_received',
  SETUP_INTENT_FAILED: 'setup_intent_failed',

  // Card submission steps
  PAYMENT_SUBMIT_CLICKED: 'payment_submit_clicked',
  STRIPE_CONFIRM_STARTED: 'stripe_confirm_started',

  // 3D Secure steps (CRITICAL for debugging)
  THREE_DS_CHALLENGE_PRESENTED: '3ds_challenge_presented',
  THREE_DS_AUTHENTICATION_STARTED: '3ds_authentication_started',
  THREE_DS_AUTHENTICATION_SUCCESS: '3ds_authentication_success',
  THREE_DS_AUTHENTICATION_FAILED: '3ds_authentication_failed',
  THREE_DS_RETURN_TO_SITE: '3ds_return_to_site',

  // Payment method creation
  PAYMENT_METHOD_CREATED: 'payment_method_created',
  PAYMENT_METHOD_SAVE_STARTED: 'payment_method_save_started',
  PAYMENT_METHOD_SAVED: 'payment_method_saved',
  PAYMENT_METHOD_SAVE_FAILED: 'payment_method_save_failed',

  // Course creation steps
  COURSE_CREATION_STARTED: 'course_creation_started',
  COURSE_PARAMS_BUILT: 'course_params_built',
  COURSE_API_CALL_STARTED: 'course_api_call_started',
  COURSE_CREATED_SUCCESS: 'course_created_success',
  COURSE_CREATION_FAILED: 'course_creation_failed',

  // Photo upload
  PHOTOS_UPLOAD_STARTED: 'photos_upload_started',
  PHOTOS_UPLOADED: 'photos_uploaded',

  // Final steps
  REDIRECT_TO_CONFIRMATION: 'redirect_to_confirmation',
  BLANK_PAGE_DETECTED: 'blank_page_detected',

  // Errors
  STRIPE_ERROR: 'stripe_error',
  API_ERROR: 'api_error',
  UNKNOWN_ERROR: 'unknown_error',
};

/**
 * Log a payment step with context
 *
 * @param {string} step - Step identifier from PAYMENT_STEPS
 * @param {object} context - Additional context data
 * @param {string} level - Sentry level: 'info', 'warning', 'error'
 */
export const logPaymentStep = (step, context = {}, level = 'info') => {
  const timestamp = new Date().toISOString();

  // Add breadcrumb for Sentry
  Sentry.addBreadcrumb({
    category: 'payment',
    message: step,
    level,
    data: {
      ...context,
      timestamp,
    },
  });

  // Log to console for development
  const emoji = level === 'error' ? '❌' : level === 'warning' ? '⚠️' : '💳';
  console.log(`${emoji} [PAYMENT] ${step}`, {
    timestamp,
    ...context,
  });

  // For critical steps or errors, send to Sentry immediately
  const criticalSteps = [
    PAYMENT_STEPS.THREE_DS_AUTHENTICATION_SUCCESS,
    PAYMENT_STEPS.THREE_DS_RETURN_TO_SITE,
    PAYMENT_STEPS.PAYMENT_METHOD_SAVE_FAILED,
    PAYMENT_STEPS.COURSE_CREATION_FAILED,
    PAYMENT_STEPS.BLANK_PAGE_DETECTED,
    PAYMENT_STEPS.STRIPE_ERROR,
    PAYMENT_STEPS.API_ERROR,
    PAYMENT_STEPS.UNKNOWN_ERROR,
  ];

  if (criticalSteps.includes(step)) {
    Sentry.captureMessage(`[PAYMENT] ${step}`, {
      level,
      tags: {
        payment_step: step,
        client_id: context.client_id,
        zone_id: context.zone_id,
      },
      extra: context,
    });
  }
};

/**
 * Log a payment error with full context
 *
 * @param {Error|string} error - Error object or message
 * @param {string} step - Step where error occurred
 * @param {object} context - Additional context
 */
export const logPaymentError = (error, step, context = {}) => {
  const errorMessage = (error && error.message) || error;
  const errorCode = (error && error.code) || 'unknown';

  console.error(`❌ [PAYMENT ERROR] ${step}:`, error, context);

  // Add breadcrumb
  Sentry.addBreadcrumb({
    category: 'payment_error',
    message: `${step}: ${errorMessage}`,
    level: 'error',
    data: {
      error_code: errorCode,
      ...context,
    },
  });

  // Capture exception
  if (error instanceof Error) {
    Sentry.captureException(error, {
      tags: {
        payment_step: step,
        error_code: errorCode,
      },
      extra: context,
    });
  } else {
    Sentry.captureMessage(`[PAYMENT ERROR] ${step}: ${errorMessage}`, {
      level: 'error',
      tags: {
        payment_step: step,
        error_code: errorCode,
      },
      extra: context,
    });
  }
};

/**
 * Set user context for payment tracking
 *
 * @param {object} client - Client object
 * @param {object} zone - Zone object
 */
export const setPaymentUserContext = (client, zone) => {
  if (!client) return;

  // Configure user scope for Sentry
  Sentry.configureScope((scope) => {
    scope.setUser({
      id: client.id,
      email: client.email,
      username: `${client.prenom} ${client.nom}`,
    });

    // Use setExtra instead of setContext for compatibility
    scope.setExtra('payment_context', {
      client_id: client.id,
      client_email: client.email,
      zone_id: zone && zone.id,
      zone_name: zone && zone.name,
      zone_light: zone && zone.light,
    });
  });
};

/**
 * Track 3D Secure flow specifically
 *
 * @param {string} status - 3DS status
 * @param {object} details - 3DS details
 */
export const log3DSFlow = (status, details = {}) => {
  const step = {
    challenge_presented: PAYMENT_STEPS.THREE_DS_CHALLENGE_PRESENTED,
    authentication_started: PAYMENT_STEPS.THREE_DS_AUTHENTICATION_STARTED,
    authentication_success: PAYMENT_STEPS.THREE_DS_AUTHENTICATION_SUCCESS,
    authentication_failed: PAYMENT_STEPS.THREE_DS_AUTHENTICATION_FAILED,
    return_to_site: PAYMENT_STEPS.THREE_DS_RETURN_TO_SITE,
  }[status];

  if (step) {
    logPaymentStep(
      step,
      {
        ...details,
        three_ds_status: status,
      },
      status.includes('failed') ? 'error' : 'info',
    );
  }
};

/**
 * Detect and log blank page issues
 * This should be called on page load to detect if user returned from 3DS to blank page
 */
export const detectBlankPageAfter3DS = () => {
  // Check if we're returning from 3DS (URL might contain specific params)
  const urlParams = new URLSearchParams(window.location.search);
  const hasStripeParams =
    urlParams.has('setup_intent') ||
    urlParams.has('setup_intent_client_secret') ||
    urlParams.has('payment_intent');

  // Check if page is essentially blank (no main content rendered)
  const hasContent = document.body.innerHTML.length > 1000; // Arbitrary threshold

  if (hasStripeParams && !hasContent) {
    logPaymentStep(
      PAYMENT_STEPS.BLANK_PAGE_DETECTED,
      {
        url: window.location.href,
        referrer: document.referrer,
        body_length: document.body.innerHTML.length,
        url_params: Object.fromEntries(urlParams),
      },
      'error',
    );
  }
};

/**
 * Create a payment session ID for tracking entire flow
 */
export const createPaymentSessionId = () => {
  const sessionId = `pay_${Date.now()}_${Math.random()
    .toString(36)
    .substr(2, 9)}`;
  sessionStorage.setItem('payment_session_id', sessionId);
  return sessionId;
};

/**
 * Get current payment session ID
 */
export const getPaymentSessionId = () => {
  return sessionStorage.getItem('payment_session_id') || 'unknown';
};

/**
 * Clear payment session
 */
export const clearPaymentSession = () => {
  sessionStorage.removeItem('payment_session_id');
};

export default {
  logPaymentStep,
  logPaymentError,
  setPaymentUserContext,
  log3DSFlow,
  detectBlankPageAfter3DS,
  createPaymentSessionId,
  getPaymentSessionId,
  clearPaymentSession,
  PAYMENT_STEPS,
};
